import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_TASK_PRIORITY } from '@shared/api-client';

import type { TaskPriority, TaskSize } from '../../../api/types';
import { Button } from '../../../shared/ui/Button';
import styles from './TaskForm.module.css';

const DEFAULT_TASK_SIZE: TaskSize = 'MEDIUM';

const SIZE_OPTIONS: TaskSize[] = ['SMALL', 'MEDIUM', 'BIG'];
const PRIORITY_OPTIONS: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH'];

// PARA-132 (AD-7, AC-10) — граница совпадает с backend-схемой
// (api/schemas.py:42): 200 символов допустимы, предупреждение с 201-го.
// Проверка применяется к ЛЮБОМУ источнику ввода — ручному и голосовому.
export const TITLE_MAX_LENGTH = 200;

// PARA-135 (РЕШ-29, AC-11a, AC-11b, AC-33) — клиентские границы оценки
// длительности, включительно. Обе дублируют серверные: `TaskCreate` и
// `TaskUpdate` объявляют `gt=0, le=1440` (РЕШ-29 снял прежнюю асимметрию, при
// которой верхняя граница жила только на клиенте). Дублирование намеренное:
// клиент даёт раннюю обратную связь и экономит round-trip, сервер защищает от
// запросов в обход Mini App (ТД-15).
export const DURATION_MIN = 1;
export const DURATION_MAX = 1440;

const DIGITS_ONLY = /^\d+$/;

export interface TaskFormValues {
  title: string;
  size: TaskSize;
  priority: TaskPriority;
  // null = «не задано»: пустое поле ошибкой не является. PARA-135 (AC-31):
  // очистка непустого значения уезжает в PATCH явным null и сбрасывает оценку
  // на сервере; в патч не попадает только НЕизменённое значение (AC-E17).
  estimatedDurationMinutes: number | null;
}

interface TaskFormProps {
  initial?: Partial<TaskFormValues>;
  submitLabel: string;
  onSubmit: (values: TaskFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  // Текст ошибки backend/сети; форма НЕ очищает введённые данные (AC-E2).
  error?: string | null;
  // PARA-132 (AD-6) — слот для голосового ввода рядом с полем title. Render-prop
  // отдаёт наружу актуальное значение и его сеттер, поэтому транскрипт меняет
  // значение УЖЕ смонтированной формы (AC-6), а форма остаётся презентационной:
  // про запись, биллинг и auth она по-прежнему не знает.
  renderVoiceInput?: (api: { title: string; setTitle: (next: string) => void }) => ReactNode;
}

// PARA-128 (FE-7) — переиспользуемая презентационная форма задачи:
// title + size + priority, валидация пустого title (AC-E1), submit/cancel.
// PARA-128 (AC-3c): подписи опций размера берутся из form.size.* — тот же
// набор ключей, что и на экране деталей, чтобы формулировки совпадали.
export function TaskForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  isPending,
  error,
  renderVoiceInput,
}: TaskFormProps): JSX.Element {
  const { t } = useTranslation('tasks');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [size, setSize] = useState<TaskSize>(initial?.size ?? DEFAULT_TASK_SIZE);
  const [priority, setPriority] = useState<TaskPriority>(
    initial?.priority ?? DEFAULT_TASK_PRIORITY,
  );
  // АР-8: состояние — строка, а не число. type="number" даёт спиннеры,
  // пропускает 'e'/'+'/'-'/дробные и отдаёт NaN — краевые случаи AC-11a стали
  // бы непроверяемыми.
  const [durationInput, setDurationInput] = useState(
    initial?.estimatedDurationMinutes != null ? String(initial.estimatedDurationMinutes) : '',
  );

  const trimmedTitle = title.trim();
  const isTitleEmpty = trimmedTitle.length === 0;
  // AC-10: текст НЕ обрезается автоматически — сокращает его пользователь.
  const isTitleTooLong = trimmedTitle.length > TITLE_MAX_LENGTH;

  const trimmedDuration = durationInput.trim();
  const isDurationEmpty = trimmedDuration.length === 0;
  const parsedDuration = DIGITS_ONLY.test(trimmedDuration) ? Number(trimmedDuration) : null;
  // AC-11b: границы включительные — off-by-one (`< DURATION_MAX`) был бы дефектом.
  const isDurationInvalid =
    !isDurationEmpty &&
    (parsedDuration === null || parsedDuration < DURATION_MIN || parsedDuration > DURATION_MAX);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (isTitleEmpty || isTitleTooLong || isDurationInvalid) {
      return;
    }
    onSubmit({
      title: trimmedTitle,
      size,
      priority,
      estimatedDurationMinutes: isDurationEmpty ? null : parsedDuration,
    });
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <label className={styles.field}>
        <span className={styles.label}>{t('form.titleLabel')}</span>
        <input
          className={styles.input}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('form.titlePlaceholder')}
        />
      </label>

      {renderVoiceInput !== undefined && (
        <div className={styles.voiceSlot}>{renderVoiceInput({ title, setTitle })}</div>
      )}

      <div className={styles.field}>
        <span className={styles.label}>{t('form.sizeLabel')}</span>
        <div className={styles.segmented} role="group" aria-label={t('form.sizeLabel')}>
          {SIZE_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={option === size ? styles.segmentActive : styles.segment}
              aria-pressed={option === size}
              onClick={() => setSize(option)}
            >
              {t(`form.size.${option}`)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.label}>{t('form.priorityLabel')}</span>
        <div className={styles.segmented} role="group" aria-label={t('form.priorityLabel')}>
          {PRIORITY_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={option === priority ? styles.segmentActive : styles.segment}
              aria-pressed={option === priority}
              onClick={() => setPriority(option)}
            >
              {t(`form.priority.${option}`)}
            </button>
          ))}
        </div>
      </div>

      {/* PARA-135 (AC-28, РЕШ-32): поле показывается ВСЕГДА — форма единообразна
          на создании и на правке. Прежний проп withDurationField удалён вместе
          с АР-7: его основание (backend не принимал поле при создании) снято
          Группой AC 7. */}
      <label className={styles.field}>
        <span className={styles.label}>{t('form.durationLabel')}</span>
        <input
          className={styles.input}
          inputMode="numeric"
          value={durationInput}
          onChange={(event) => setDurationInput(event.target.value)}
          placeholder={t('form.durationPlaceholder')}
        />
      </label>

      {isTitleEmpty && <p className={styles.hint}>{t('form.errors.titleRequired')}</p>}
      {isTitleTooLong && (
        <p className={styles.error}>{t('form.errors.titleTooLong', { max: TITLE_MAX_LENGTH })}</p>
      )}
      {isDurationInvalid && (
        <p className={styles.error}>
          {t('form.errors.durationInvalid', { min: DURATION_MIN, max: DURATION_MAX })}
        </p>
      )}
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={isPending || isTitleEmpty || isTitleTooLong || isDurationInvalid}
        >
          {submitLabel}
        </Button>
        <Button type="button" variant="ghost" fullWidth onClick={onCancel} disabled={isPending}>
          {t('form.cancel')}
        </Button>
      </div>
    </form>
  );
}
